import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  OrdersState,
  OrderItem,
  Order,
  OrderFilters,
  CurrentOrderState,
  OrderItemModifier,
} from "./types";
import {
  OrderStatus,
  OrderType,
  Product,
} from "@/types/generated/graphql-types";
import { calculateCartTotal, generateItemUUID } from "./utils";

const initialCurrentOrder: CurrentOrderState = {
  items: [],
  sum: 0,
  comment: "",
  loading: false,
  error: null,
};

const initialState: OrdersState = {
  currentOrder: initialCurrentOrder,
  orders: [],
  orderHistory: {
    items: [],
    loading: false,
    hasMore: false,
    filters: {},
  },
  loading: false,
  error: null,
};

export const ordersReducer = createSlice({
  name: "orders",
  initialState,
  reducers: {
    // ===== CART OPERATIONS =====

    addPosition: (
      state,
      action: PayloadAction<{
        dish: Product;
        quantity?: number;
        modifiers?: OrderItemModifier[];
        comment?: string;
      }>
    ) => {
      const {
        dish,
        quantity = 1,
        modifiers = [],
        comment = "",
      } = action.payload;

      const newItem: OrderItem = {
        uuid: generateItemUUID(),
        dish,
        qty: quantity,
        price: dish.pricePoint || 0, // Use pricePoint from GraphQL
        modifiers,
        comment,
      };

      state.currentOrder.items.push(newItem);
      state.currentOrder.sum = calculateCartTotal(state.currentOrder.items);
      state.currentOrder.error = null;
    },

    removeOrderItem: (state, action: PayloadAction<string>) => {
      const itemUUID = action.payload;
      state.currentOrder.items = state.currentOrder.items.filter(
        (item) => item.uuid !== itemUUID
      );
      state.currentOrder.sum = calculateCartTotal(state.currentOrder.items);
      state.currentOrder.error = null;
    },

    updateOrderItem: (
      state,
      action: PayloadAction<{
        itemUUID: string;
        updates: Partial<Pick<OrderItem, "qty" | "modifiers" | "comment">>;
      }>
    ) => {
      const { itemUUID, updates } = action.payload;
      const itemIndex = state.currentOrder.items.findIndex(
        (item) => item.uuid === itemUUID
      );

      if (itemIndex !== -1) {
        // Update item with new values
        const item = state.currentOrder.items[itemIndex];
        state.currentOrder.items[itemIndex] = {
          ...item,
          ...updates,
          // Ensure quantity is at least 1
          qty: updates.qty !== undefined ? Math.max(1, updates.qty) : item.qty,
        };

        // Recalculate total
        state.currentOrder.sum = calculateCartTotal(state.currentOrder.items);
        state.currentOrder.error = null;
      }
    },

    updateQuantity: (
      state,
      action: PayloadAction<{ itemUUID: string; quantity: number }>
    ) => {
      const { itemUUID, quantity } = action.payload;
      const itemIndex = state.currentOrder.items.findIndex(
        (item) => item.uuid === itemUUID
      );

      if (itemIndex !== -1 && quantity > 0) {
        state.currentOrder.items[itemIndex].qty = quantity;
        state.currentOrder.sum = calculateCartTotal(state.currentOrder.items);
        state.currentOrder.error = null;
      }
    },

    updateOrderComment: (state, action: PayloadAction<string>) => {
      state.currentOrder.comment = action.payload;
    },

    clearOrder: (state) => {
      state.currentOrder = { ...initialCurrentOrder };
    },

    // ===== ORDER LIFECYCLE OPERATIONS =====

    createOrderStart: (state) => {
      state.loading = true;
      state.error = null;
      state.currentOrder.loading = true;
    },

    createOrderSuccess: (state, action: PayloadAction<Order>) => {
      state.loading = false;
      state.currentOrder.loading = false;
      state.orders.push(action.payload);

      // Clear current order after successful creation
      state.currentOrder = { ...initialCurrentOrder };
    },

    createOrderError: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.currentOrder.loading = false;
      state.error = action.payload;
      state.currentOrder.error = action.payload;
    },

    // ===== ORDER UPDATE OPERATIONS =====

    updateOrderStart: (state) => {
      state.loading = true;
      state.error = null;
    },

    updateOrderSuccess: (state, action: PayloadAction<Order>) => {
      state.loading = false;
      const updatedOrder = action.payload;

      // Update order in orders array
      const orderIndex = state.orders.findIndex(
        (order) => order.id === updatedOrder.id
      );
      if (orderIndex !== -1) {
        state.orders[orderIndex] = updatedOrder;
      }

      // Update order in history if it exists there
      const historyIndex = state.orderHistory.items.findIndex(
        (order) => order.id === updatedOrder.id
      );
      if (historyIndex !== -1) {
        state.orderHistory.items[historyIndex] = updatedOrder;
      }
    },

    updateOrderError: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // ===== ORDER LOADING OPERATIONS =====

    loadOrdersStart: (state) => {
      state.loading = true;
      state.error = null;
    },

    loadOrdersSuccess: (state, action: PayloadAction<Order[]>) => {
      state.loading = false;
      state.orders = action.payload;
    },

    loadOrdersError: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    loadOrderStart: (state) => {
      state.loading = true;
      state.error = null;
    },

    loadOrderSuccess: (state, action: PayloadAction<Order>) => {
      state.loading = false;
      const order = action.payload;

      // Add or update order in orders array
      const existingIndex = state.orders.findIndex((o) => o.id === order.id);
      if (existingIndex !== -1) {
        state.orders[existingIndex] = order;
      } else {
        state.orders.push(order);
      }
    },

    loadOrderError: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // ===== ORDER HISTORY OPERATIONS =====

    loadOrderHistoryStart: (state) => {
      state.orderHistory.loading = true;
    },

    loadOrderHistorySuccess: (
      state,
      action: PayloadAction<{
        orders: Order[];
        hasMore: boolean;
        append?: boolean;
      }>
    ) => {
      const { orders, hasMore, append = false } = action.payload;
      state.orderHistory.loading = false;

      if (append) {
        state.orderHistory.items = [...state.orderHistory.items, ...orders];
      } else {
        state.orderHistory.items = orders;
      }

      state.orderHistory.hasMore = hasMore;
    },

    loadOrderHistoryError: (state, action: PayloadAction<string>) => {
      state.orderHistory.loading = false;
      state.error = action.payload;
    },

    setOrderFilters: (state, action: PayloadAction<OrderFilters>) => {
      state.orderHistory.filters = action.payload;
      // Reset pagination when filters change
      state.orderHistory.hasMore = false;
    },

    loadMoreOrders: (state) => {
      // This will be handled by saga
      state.orderHistory.loading = true;
    },

    // ===== GENERAL STATE MANAGEMENT =====

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    setCartLoading: (state, action: PayloadAction<boolean>) => {
      state.currentOrder.loading = action.payload;
    },

    setCartError: (state, action: PayloadAction<string | null>) => {
      state.currentOrder.error = action.payload;
    },
  },
});

export const {
  // Cart operations
  addPosition,
  removeOrderItem,
  updateOrderItem,
  updateQuantity,
  updateOrderComment,
  clearOrder,

  // Order lifecycle
  createOrderStart,
  createOrderSuccess,
  createOrderError,

  // Order updates
  updateOrderStart,
  updateOrderSuccess,
  updateOrderError,

  // Order loading
  loadOrdersStart,
  loadOrdersSuccess,
  loadOrdersError,
  loadOrderStart,
  loadOrderSuccess,
  loadOrderError,

  // Order history
  loadOrderHistoryStart,
  loadOrderHistorySuccess,
  loadOrderHistoryError,
  setOrderFilters,
  loadMoreOrders,

  // General state
  setLoading,
  setError,
  setCartLoading,
  setCartError,
} = ordersReducer.actions;

export default ordersReducer.reducer;
